import {
  CalendarDotsIcon,
  ClockIcon,
  HandPeaceIcon,
  type IconProps,
} from "@phosphor-icons/react";
import dayjs from "dayjs";
import { useIntl } from "react-intl";

import { DrawerTopBar } from "@/components/Drawer/DrawerTopBar";
import { SwipeableDrawer } from "@/components/Drawer/SwipeableDrawer";
import { PaymentDetails, PaymentDetailsRow } from "@/components/PaymentDetails";
import { usePaymentContext } from "@/contexts/PaymentContext";
import { calculateMonthlyInstallment } from "@/utils/bnpl-fee";
import { formatCurrency } from "@/utils/currency";

type InstallmentDescrptionRowProps = {
  label: React.ReactNode;
  description: React.ReactNode;
  icon: React.ComponentType<IconProps>;
};

const InstallmentDescrptionRow = ({
  label,
  description,
  icon: Icon,
}: InstallmentDescrptionRowProps) => {
  return (
    <div className="flex gap-4">
      <Icon className="size-10 shrink-0 rounded-2xl border border-core-accent-200 bg-core-accent-100 p-2 text-text-interactive" />
      <div>
        <div className="mb-1 text-base font-medium text-text-primary">
          {label}
        </div>
        <div className="text-sm text-text-tertiary">{description}</div>
      </div>
    </div>
  );
};

interface InstallmentBreakdownDrawerProps {
  totalPrice: number;
  open: boolean;
  onClose: () => void;
}

export const InstallmentBreakdownDrawer = ({
  totalPrice,
  open,
  onClose,
}: InstallmentBreakdownDrawerProps) => {
  const intl = useIntl();
  const { selectedInstallments } = usePaymentContext();

  const nextPaymentMonth = dayjs().add(1, "month").toDate();

  return (
    <SwipeableDrawer
      open={open}
      onClose={onClose}
      backgroundColor="var(--color-surface-background)"
      disableRestoreFocus
    >
      <DrawerTopBar
        onCloseClick={onClose}
        title={intl.formatMessage({
          defaultMessage: "installment details",
          description: "Heading for the installment details drawer",
        })}
      />

      <PaymentDetails
        title={intl.formatMessage({
          defaultMessage: "installments breakdown",
          description:
            "Section heading for the detailed installment payment breakdown",
        })}
      >
        <PaymentDetailsRow
          label={intl.formatMessage({
            defaultMessage: "Number of installments",
            description:
              "Label for the number of installments in the payment plan",
          })}
          value={selectedInstallments}
        />

        <PaymentDetailsRow
          label={intl.formatMessage({
            defaultMessage: "Next payment",
            description:
              "Label for the date of the next scheduled installment payment",
          })}
          value={intl.formatDate(nextPaymentMonth, { month: "long" })}
        />

        <PaymentDetailsRow
          label={intl.formatMessage({
            defaultMessage: "Monthly payment",
            description:
              "Label for the monthly installment amount in the payment breakdown",
          })}
          value={formatCurrency(
            calculateMonthlyInstallment(totalPrice, selectedInstallments),
          )}
        />

        <PaymentDetailsRow
          label={intl.formatMessage({
            defaultMessage: "Total price to be paid",
            description:
              "Label for the total price to be paid in the installment breakdown",
          })}
          value={formatCurrency(totalPrice)}
        />
      </PaymentDetails>

      <div className="section-card mt-2">
        <div className="mb-6 text-lg font-medium text-text-primary">
          {intl.formatMessage({
            defaultMessage: "How installments work",
            description:
              "Header for the section explaining how installments work",
          })}
        </div>

        <div className="-my-4 divide-y divide-stroke-neutral-soft *:py-4">
          <InstallmentDescrptionRow
            label={intl.formatMessage({
              defaultMessage: "First payment",
              description: "Label for the first installment payment",
            })}
            description={intl.formatMessage({
              defaultMessage:
                "Your first installment is charged from your QI Card when your booking is confirmed by our team.",
              description: "Description for the first installment payment",
            })}
            icon={ClockIcon}
          />

          <InstallmentDescrptionRow
            label={intl.formatMessage({
              defaultMessage: "Monthly auto-charge",
              description:
                "Label for the monthly auto-charge installment payment",
            })}
            description={intl.formatMessage({
              defaultMessage:
                "Remaining payments are automatically deducted each month. No action needed.",
              description:
                "Description for the monthly auto-charge installment payment",
            })}
            icon={CalendarDotsIcon}
          />

          <InstallmentDescrptionRow
            label={intl.formatMessage({
              defaultMessage: "We've got your back",
              description:
                "Label for the support provided during the installment payment process",
            })}
            description={intl.formatMessage({
              defaultMessage:
                "Just confirm your booking at checkout, and the rest is easy.",
              description:
                "Description for the support provided during the installment payment process",
            })}
            icon={HandPeaceIcon}
          />
        </div>
      </div>
    </SwipeableDrawer>
  );
};
